import React from 'react';
import { DashboardMenu } from '../dashboard-menu/index';
import { Footer } from '../footer/index';
import { colors } from '../../../style';
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import styled from 'styled-components';
import {
  enums,
  getLocalizedOrDefault,
  prefixObjectValues,
  scrollApp,
} from '../../../utils';
import ContactUs from '../../contact-us';
import Messages from '../../messages';
import NewUploadDocuments from '../../new-upload';
import Documents from '../../documents';
import Page from '../../page';
import Profile from '../../profile';
import { LoadingScreen } from '../loading-screen';
import { FatalError } from '../exception-screens/fatal-error';
import { log } from '../../../utils/log';
import { CommonModal } from '../common-modal';
import ConfirmDialogModal from '../confirm-dialog-modal';
import BrowserWarningModal from '../browser-warning-modal';
import { SessionTimeoutModal } from '../session-timeout-modal';
import ClaimsLeavePlan from '../claim-card/claims-leave-plan';
import StartAClaim from '../../start-a-claim';
import AbsenceHistory from '../../absence-history';
import Banner from '../banner';
import LearningHub from '../../learning-hub';
import {
  KenticoDataContext,
  IKenticoData,
} from '../../../contexts/kentico-data-context';
import { trackGA4PageView, initAnalytics } from '../../../utils/analytics';
import Supplemental from '../../supplemental';
import AvailableTime from '../../available-time';
import { logout } from '../../../utils/auth-manager';
import { SummaryContainer } from '../../summary/container';
import { TasksContainer } from '../../tasks/container';
import { FormContainer } from '../../forms/container';
import { PaymentsContainer } from '../../payments/container';
import { PersonDataContext } from '../../../contexts/person-data-context';
import { createGA4Data } from '../../../utils/analytics/helpers';
import DuplicateClaims from '../../duplicateClaims';
import { ExternalRedirect } from '../../external-redirect';
import RefreshOverlay from '../refresh-overlay';
import { RefreshContext } from '../../../contexts/refresh-context';
import { useLDClient } from '@launchdarkly/react-sdk';
import '@unum/uc/dist/index.js';
import '@unum/uc/dist/uc.css';
import envConfig from '../../../utils/env-config';

const TaskDetail = React.lazy(() => import('../../tasks/task-detail'));
const TaskLlDescription = React.lazy(
  () => import('../../tasks/task-detail/task-ll-description'),
);
const UserInitiatedTaskContainer = React.lazy(() =>
  import('../../tasks/user-initated-task/container').then((mod) => ({
    default: mod.UserInitiatedTaskContainer,
  })),
);

// const log = new Logger\('app');

interface AppProps {
  keyId?: string;
  notRegisterable?: boolean;
  failedWithData?: boolean;
}

export interface AppRoute {
  breadcrumb: string;
  path: string;
}

interface UserInfo {
  email?: string;
  firstName?: string;
  lastName?: string;
  eid?: string;
}

const DEFAULT_LINK_DISCLAIMER =
  'The website you will be entering is not owned or operated by, and its content, products and information are not owned ' +
  'or controlled by, Unum and/or its subsidiaries. Unum makes no representations about, does not endorse, and is not responsible ' +
  "or liable for damages relating to the third party's products or services, its website, its privacy policies or practices, or " +
  'the content of the third party website. Please be aware that the security and privacy policies on these sites may be different ' +
  "than Unum's policies, so please read third party privacy and security policies closely. If you have any questions or concerns " +
  'about the products and services offered on linked third party websites, please contact the third party directly. <br/><br/>By ' +
  'clicking on the <b>CONTINUE</b> button below, you acknowledge the above statement and will be taken to the linked site. If you ' +
  "want to remain on Unum's site, select the <b>CANCEL</b> button.";

let lastPageView: { key: string; timestamp: number } | undefined;
const PAGE_VIEW_DEDUPE_WINDOW_MS = 1000;

const domIdsStatic = {
  rootNode: 'app-root-node',
};

const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

export const Container = styled.div`
  background-color: ${colors.background};
  height: 100%;
  min-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-flow: column;
  flex-direction: row;
  flex-wrap: wrap;
`;

export const MainContainer = styled.main`
  flex-grow: 1;
  justify-content: space-between;
  padding: 12px 3rem;
  position: relative;
  @media (max-width: 1024px) {
    padding: 1rem 1.5rem;
  }
  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 400px) {
    padding: 16px 10px;
  }
`;

const FlexBreak = styled.div`
  flex-basis: 100%;
  height: 0;
`;

export const PortalContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
`;

const onUnload = () => {
  if (global.connectSession) {
    global.connectSession.disconnectParticipant();
    logout();

    const start = Date.now();
    let now = start;
    while (now - start < 1000) {
      now = Date.now();
    }
  }
};

const getDocumentTitle = (
  pathname: string,
  config: IKenticoData[enums.KenticoDataKeys.config],
) => {
  const defaultTitle = 'Unum Total Leave';
  const documentTitleMapRaw = config[enums.RemoteConfigKeys.documentTitles];
  try {
    const documentTitleMap = JSON.parse(documentTitleMapRaw);

    return documentTitleMap[pathname]
      ? documentTitleMap[pathname]
      : defaultTitle;
  } catch (e) {
    log.error(e);
    return defaultTitle;
  }
};

const onPathChange = (
  pathname: string,
  config: IKenticoData[enums.KenticoDataKeys.config],
) => {
  const ntnIndex = pathname.lastIndexOf('/NTN');
  const baseURL =
    ntnIndex > -1 ? pathname.slice(0, pathname.lastIndexOf('/NTN')) : pathname;
  document.title = getDocumentTitle(baseURL, config);
};

export const handleUseEffectMount = async (
  setUserInfo: React.Dispatch<React.SetStateAction<{}>>,
  setRedirectPath: React.Dispatch<React.SetStateAction<string | undefined>>,
  config: IKenticoData[enums.KenticoDataKeys.config],
) => {
  //Get initial title
  onPathChange(globalThis.location.pathname, config);
  const uiString = sessionStorage.getItem(enums.CacheKeys.userInfo) ?? '';

  if (uiString?.length > 0) {
    setUserInfo(JSON.parse(uiString));
  }

  const entryPoint = sessionStorage.getItem(enums.CacheKeys.entryPoint);

  if (entryPoint && entryPoint !== globalThis.location.pathname) {
    setRedirectPath(entryPoint);
  }

  // if we are loading the app, we know we are logged in and can safely
  // remove this from storage at this point, no matter the outcome of
  // the previous statement
  sessionStorage.removeItem(enums.CacheKeys.entryPoint);

  return () => {};
};

//In case we can't get the configuration value from Kentico
const defaultApprovedURLList =
  'behavioralhealth.unum.com|portal-dev.unum.com|portal-itest.unum.com|portal-ua.unum.com|portal.unum.com|' +
  'preview-assets-us-01.kc-usercontent.com|www.unum.com|unmphenxphoenixdevd109-getdocument-cache.s3.amazonaws.com' +
  'assets-us-01.kc-usercontent.com|unmphenxphoenixprodp109-getdocument-cache';

export const disclaimerModal = (
  e: MouseEvent,
  setDisplayDisclaimer: React.Dispatch<React.SetStateAction<boolean>>,
  setContinueLink: React.Dispatch<React.SetStateAction<string>>,
  config: IKenticoData[enums.KenticoDataKeys.config],
) => {
  const anchor = (e.target as HTMLElement).closest('a');
  if (!anchor) {
    return;
  }

  const approvedURLStr = config['approved_url_list'] || defaultApprovedURLList;
  const approvedURLArray = approvedURLStr.split('|');

  const linkType = anchor.getAttribute('data-link-type');
  const showDisclaimer =
    linkType === 'external' || !approvedURLArray.includes(anchor.hostname);

  if (anchor.hostname && showDisclaimer) {
    e.preventDefault();
    setDisplayDisclaimer(true);
    setContinueLink(anchor.href);
  }
};

const App: (p: AppProps) => React.ReactElement = (props) => {
  const p = enums.AppPaths;
  const domIds = domIdsUnique(props.keyId);
  const [userInfo, setUserInfo] = React.useState<UserInfo>({});
  const [displayDisclaimer, setDisplayDisclaimer] = React.useState(false);
  const [continueLink, setContinueLink] = React.useState<string>('');
  const [displayFailedWithDataError, setDisplayFailedWithDataError] =
    React.useState(props.failedWithData || false);
  const { config, dynamicContent, localizedStrings } =
    React.useContext(KenticoDataContext);
  const { personData, vbLoading, analyticsDataReady } =
    React.useContext(PersonDataContext);
  const { status: refreshStatus, usingCachedData } =
    React.useContext(RefreshContext);
  const [redirectPath, setRedirectPath] = React.useState<string>();
  const { pathname, search, hash } = useLocation();
  const history = useHistory();
  const hasTrackedFirstView = React.useRef(false);
  const lastTrackedPageViewRef = React.useRef<string>('');
  const personDataRef = React.useRef(personData);
  const ldClient = useLDClient();

  const trackPageView = React.useCallback(
    (locationOverride?: { pathname: string; search: string; hash: string }) => {
      const locationToTrack = locationOverride ?? {
        pathname,
        search,
        hash,
      };
      const pageKey = `${locationToTrack.pathname}${locationToTrack.search}${locationToTrack.hash}`;

      if (lastTrackedPageViewRef.current === pageKey) {
        return;
      }

      const latest = personDataRef.current;
      const dedupeKey = `${latest?.person?.eid ?? ''}:${pageKey}`;
      const now = Date.now();
      if (
        lastPageView?.key === dedupeKey &&
        now - lastPageView.timestamp < PAGE_VIEW_DEDUPE_WINDOW_MS
      ) {
        lastTrackedPageViewRef.current = pageKey;
        return;
      }

      const hasCommonLandingPage =
        ldClient?.boolVariation('enable-clp-header', true) ?? false;
      trackGA4PageView(createGA4Data(latest, hasCommonLandingPage));
      lastPageView = { key: dedupeKey, timestamp: now };
      lastTrackedPageViewRef.current = pageKey;
    },
    [hash, ldClient, pathname, search],
  );

  React.useEffect(() => {
    const clickListener = (e: MouseEvent) => {
      disclaimerModal(e, setDisplayDisclaimer, setContinueLink, config);
    };
    document.body.addEventListener('click', clickListener);
    window.addEventListener('beforeunload', onUnload);

    handleUseEffectMount(setUserInfo, setRedirectPath, config);

    const dispose = initAnalytics({});

    const chatWidget = document.getElementById('amazon-connect-chat-widget');
    if (chatWidget) {
      chatWidget.style.display = 'block';
    }

    return () => {
      document.body.removeEventListener('click', clickListener);
      window.removeEventListener('beforeunload', onUnload);

      if (dispose) {
        dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const unlisten = history.listen((location) =>
      onPathChange(location.pathname, config),
    );

    return () => {
      unlisten();
    };
  }, [history, config]);

  // Keep the ref in sync with latest personData
  React.useEffect(() => {
    personDataRef.current = personData;
  }, [personData]);

  React.useEffect(() => {
    if (userInfo?.eid) {
      const companyCode = personData?.person?.companyCode;
      ldClient?.identify({
        kind: 'user',
        key: userInfo.eid,
        ...(companyCode !== undefined && { companyCode }),
      });
    }
  }, [ldClient, userInfo, personData]);

  // Track the very first view only once when personData is available
  React.useEffect(() => {
    if (hasTrackedFirstView.current) return;
    if (!personData || !analyticsDataReady) return;
    if (vbLoading) return;

    trackPageView();
    hasTrackedFirstView.current = true;
  }, [analyticsDataReady, personData, vbLoading, trackPageView]);

  // track all view changes after first one
  React.useEffect(() => {
    const unlisten = history.listen((location) => {
      if (!hasTrackedFirstView.current) {
        return;
      }

      const sameRoute =
        location.pathname === pathname &&
        location.search === search &&
        location.hash === hash;

      if (sameRoute) {
        return;
      }

      trackPageView(location);
    });

    return () => {
      unlisten();
    };
  }, [hash, history, pathname, search, trackPageView]);

  React.useEffect(() => {
    scrollApp();
  }, [pathname]);

  // If background refresh fails while using cached data, show the FAILED_WITH_DATA modal
  const wasUsingCachedRef = React.useRef(usingCachedData);
  React.useEffect(() => {
    if (usingCachedData) {
      wasUsingCachedRef.current = true;
    }
  }, [usingCachedData]);

  React.useEffect(() => {
    if (
      wasUsingCachedRef.current &&
      refreshStatus === enums.RefreshStatus.failed
    ) {
      setDisplayFailedWithDataError(true);
    }
  }, [refreshStatus]);

  return (
    <>
      {props.notRegisterable ? (
        <Redirect to={enums.AppPaths.dataLoadFailed} />
      ) : (
        <>{redirectPath && <Redirect to={redirectPath} />}</>
      )}
      <Container id={domIds.rootNode} className="fade-in-quick">
        {ldClient?.boolVariation('enable-clp-header', true) && (
          <div className="tw-w-[100vw] tw-ml-[calc(-50vw+50%)] tw-mr-[calc(-50vw+50%)] ">
            {' '}
            <uc-unum-universal-nav
              id="uc-unum-universal-nav"
              active="totalleave"
              environment={envConfig.universalNavEnvKey}
              showtotalleave
            ></uc-unum-universal-nav>
          </div>
        )}
        <Banner />
        <RefreshOverlay />
        {/* Style API endpoint for css classes for dynamic Kentico content */}
        <PortalContainer id="app-portal-container">
          <DashboardMenu />

          <MainContainer>
            <React.Suspense fallback={<LoadingScreen />}>
              <Switch>
                <Route
                  exact
                  path={p.appRoot}
                  render={() => (
                    <SummaryContainer failedWithData={props.failedWithData} />
                  )}
                />
                <Route path={p.form} component={FormContainer} />
                <Route
                  path={p.profile}
                  render={() => <Profile userEmail={userInfo.email} />}
                />
                <Route path={p.contactUs} component={ContactUs} />
                <Route path={p.messages} component={Messages} />
                {/* <Route path={p.upload} component={UploadDocuments} /> */}
                <Route path={p.newUpload} component={NewUploadDocuments} />
                <Route path={`${p.documents}/:ntn?`} component={Documents} />
                <Route
                  path={`${p.payments}/:ntn?`}
                  render={() => (
                    <PaymentsContainer failedWithData={props.failedWithData} />
                  )}
                />
                <Route path={p.time} component={AvailableTime} />
                <Route path={p.page} component={Page} />
                <Route path={p.startClaim} component={StartAClaim} />
                <Route path={`${p.tasks}/:ntn?`} component={TasksContainer} />
                <Route path={p.taskDetails} component={TaskDetail} />
                <Route
                  path={p.userInitiatedTask}
                  component={UserInitiatedTaskContainer}
                />
                <Route
                  path={p.taskLlDescription}
                  component={TaskLlDescription}
                />
                <Route path={p.loading} component={LoadingScreen} />
                <Route path={p.error} component={FatalError} />
                <Route path={p.claimsLeavePlan} component={ClaimsLeavePlan} />
                <Route path={p.absenceHistory} component={AbsenceHistory} />
                <Route path={p.learninghub} component={LearningHub} />
                <Route path={p.supplemental} component={Supplemental} />
                <Route path={p.duplicateClaim} component={DuplicateClaims} />
                <Route
                  path={`${p.externalRedirect}/:type`}
                  component={ExternalRedirect}
                />
              </Switch>
            </React.Suspense>
          </MainContainer>
        </PortalContainer>

        {displayFailedWithDataError && (
          <CommonModal
            markdown={
              `We're having trouble accessing our files so we may not be able to give you updated information right now.` +
              ` You can still ask questions or see information from the last time you logged in. You can also come back and try again later.`
            }
            confirmButtonText="Got it"
            modalDownloadURL="false"
            modalId="app.FINEOSDataLoadFailWithContinueLogin"
            small={true}
            modalCallback={() => setDisplayFailedWithDataError(false)}
          />
        )}

        <FlexBreak />
        <Footer />
        {displayDisclaimer && (
          <ConfirmDialogModal
            title={getLocalizedOrDefault(
              localizedStrings,
              'disclaimer_modal_title',
              'Disclaimer',
            )}
            description={
              (dynamicContent['external_link_disclaimer']
                ?.dcContentFull as any) || DEFAULT_LINK_DISCLAIMER
            }
            markdown={true}
            confirmButtonText={getLocalizedOrDefault(
              localizedStrings,
              'disclaimer_continue_text',
              'Continue',
            )}
            cancelButtonText={getLocalizedOrDefault(
              localizedStrings,
              'disclaimer_cancel_text',
              'Cancel',
            )}
            modalCallback={() => setDisplayDisclaimer(false)}
            handleConfirm={() => {
              if (continueLink) {
                window.open(continueLink, '_blank');
              }
            }}
            dataAttributes={{ 'data-link-url': continueLink }}
          />
        )}
        <BrowserWarningModal />
        {ldClient?.boolVariation('inactivity-logout', true) && (
          <SessionTimeoutModal />
        )}
      </Container>
    </>
  );
};

export default App;
